<!-- eslint-disable vue/no-v-html -->
<script lang="ts" setup>
import type { NbRenderer } from 'ipynb2html'
import { Document } from 'nodom'

const props = defineProps<{
  ipynb: string
  filename: string
}>()

const _ipynb: Ref<null | {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createRenderer: (doc: Document) => NbRenderer<any>
}> = ref(null)

const ipynbContent: ComputedRef<string | null> = computed(() => {
  if (!props.ipynb) return null
  const notebook = JSON.parse(props.ipynb)
  const renderNotebook = _ipynb.value?.createRenderer(new Document())
  const html = renderNotebook ? renderNotebook.render(notebook) : null
  return html?.outerHTML || null
})

onMounted(async () => {
  _ipynb.value = await import('ipynb2html')
})
</script>

<template lang="pug">
.jupyter-reader
  .jupyter-reader__filename {{ filename }}
  .jupyter-reader__content(
    v-if="ipynbContent"
    v-html="ipynbContent"
  )
</template>

<style lang="sass" scoped>
.jupyter-reader
  @apply w-full relative
  .jupyter-reader__filename
    @apply absolute top-0 left-[50%] translate-y-[-50%] translate-x-[-50%] w-fit bg-dark-700 border border-primary text-primary rounded-lg px-4 lg:px-8 py-1 text-dark-900 font-bold z-20 whitespace-nowrap
  .jupyter-reader__content
    @apply w-full max-w-[900px] bg-transparent-dark p-0 pl-4 lg:p-6 pt-10 max-h-[65vh] overflow-auto scrollbar-primary border border-primary rounded-md mx-auto text-white
  :deep(.jupyter-reader__content)
    .nb-notebook
      @apply flex flex-col gap-4 pl-7
      *
        @apply text-xs
      h1
        @apply text-primary font-bold text-2xl lg:text-4xl
      h2, h3, h4, h5, h6
        @apply text-secondary mt-3 font-bold
      h2
        @apply text-xl lg:text-2xl
      h3
        @apply text-lg lg:text-xl
      h4
        @apply text-base lg:text-lg
      .nb-input,
      .nb-output
        @apply w-full relative border p-0.5 rounded-md
        & > pre,
        .nb-html-output
          @apply w-full overflow-auto scrollbar-primary
        &::before
          @apply absolute top-0 text-sm md:text-lg font-bold
      .nb-input
        @apply border-primary
        &::before
          content: 'IN:'
          @apply text-primary -left-5 md:-left-7
      .nb-output
        @apply border-secondary mt-2 text-secondary opacity-70 hover:opacity-100
        *
          @apply text-[10px]
        &::before
          content: 'OUT:'
          @apply text-secondary -left-10 md:-left-11
      code
        .hljs-keyword
          @apply text-[#B2BABB]
        .hljs-comment
          @apply text-[#808B96]
        .hljs-string
          @apply text-[#F6DDCC]
        .hljs-literal
          @apply text-[#E8DAEF]
        .hljs-built_in
          @apply text-[#D5F5E3]
</style>
